import * as React from 'react'
// libs
import styled from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
import ReactDOMServer from 'react-dom/server'
// graphql
import { PLAN_DETAILS_TREE_QUERY, PLANS_LIST_QUERY } from 'graphql/plan/queries'
import { PLAN_BULK_ITEMS_UPDATE_MUTATION, PLAN_DELETE_MUTATION } from 'graphql/plan/mutations'
// hooks
import { useApolloClient, useBookmarks, useCurrentUser, useInitialValues, useShowModal } from 'hooks/index'
// components
import { Menu } from 'components/UI'
import PlanCloneModal from 'components/Modals/PlanCloneModal'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
import ObjectivesListPreview from 'components/PageComponents/Plans/Edit/components/ObjectivesListPreview/index'
// constants
import { Modals } from 'constants/modals'
import { PLAN_VIEW_MODE } from 'constants/plan'
import { INITIAL_PER_PAGE } from 'constants/table'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// icons
import LockIcon from '@mui/icons-material/LockOutlined'
import PrintIcon from '@mui/icons-material/Print'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
import BookmarkIcon from '@mui/icons-material/BookmarkBorderOutlined'
import DotsIconOrigin from '@mui/icons-material/MoreHorizOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
// types
import { IFieldValueMapItem } from 'types/graphql'
// themes
import { colors } from 'themes/index'
// utils
import get from 'lodash/get'
import omit from 'lodash/omit'
import pipe from 'lodash/fp/pipe'
import sortBy from 'lodash/fp/sortBy'
import { safeGetOr } from 'utils/fp'
import { handleError } from 'utils/errors/handlers'
import { size, transparentize } from 'polished'
import { getFieldsInitialValues } from 'utils/form'
import { getPlanColumnVariables } from 'utils/planColumns'

// styled
const DotsIcon = styled(DotsIconOrigin)`
  && {
    cursor: pointer;
    ${size('25px')};
  }
`

const FileInput = styled.input`
  display: none;
`

const Focuser = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1299;
  background: ${transparentize(0.5, colors.black)};
`

const PlanDetailsActionsMenu: React.FC = () => {
  const { client } = useApolloClient()

  const [deleteMutation] = useMutation(PLAN_DELETE_MUTATION)
  const [planBulkItemsUpdate] = useMutation(PLAN_BULK_ITEMS_UPDATE_MUTATION)

  const [showFocuser, setShowFocuser] = React.useState(false)

  const { plan, planDetailsRefetch, treeQueryRefetch, lockPlan } = React.useContext(PlanEditPageContext)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const {
    permissionsFromSecurityGroups: { adminLockPlans }
  } = useCurrentUser()

  const router = useRouter()

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })
  const { showModal: showCloneModal } = useShowModal({ modalType: Modals.PLAN_CLONE_MODAL })

  const createOrDeleteBookmark = useBookmarks()

  const { planDeletePlan } = plan.currentPermissions

  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const getPlanInitialValues = async () => {
    return getFieldsInitialValues({
      fieldValues: pipe(
        safeGetOr<any, any, any>([], 'fieldValueMap'),
        sortBy((value: IFieldValueMapItem) => value.updatedAt)
      )(plan),
      fields: get(plan, 'planType.fields', []),
      client
    })
  }

  const data = useInitialValues(getPlanInitialValues, plan)

  const printPlan = async (): Promise<void> => {
    enqueueSnackbar('Processing...', {
      key: 'upload',
      variant: 'progress',
      autoHideDuration: null
    })

    let planItems = []

    await treeQueryRefetch().then((response) => (planItems = get(response, 'data.planItems.data')))

    await treeQueryRefetch().then((res) => {
      const updatedTree = get(res, 'data.planItems.data', [])

      planItems = updatedTree.map((item) => ({
        ...item,
        parentId: updatedTree.some((pItem) => pItem.id === item.parentId) ? item.parentId : null
      }))
    })

    const { default: html2pdf } = await import('html2pdf.js')

    const opt = {
      margin: 8,
      filename: `${plan.name}.pdf`,
      pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf-page-break', avoid: '.tree-node' },
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 1, allowTaint: true, dpi: 300, letterRendering: true, useCORS: true },
      jsPDF: { unit: 'mm' as const, format: 'a4', orientation: 'landscape' as const }
    }

    return html2pdf()
      .set(opt)
      .from(
        ReactDOMServer.renderToString(
          <ObjectivesListPreview plan={{ ...plan, ...omit(data, 'planType') }} planItems={planItems} />
        )
      )
      .save()
      .then(() => closeSnackbar('upload'))
  }

  const onDeletePlan = async () => {
    try {
      await deleteMutation({
        variables: { id: plan.id },
        refetchQueries: [
          {
            query: PLANS_LIST_QUERY,
            variables: {
              pagination: {
                pageSkip: 0,
                pageTake: INITIAL_PER_PAGE
              }
            }
          }
        ]
      })

      router.back()
    } catch (errors) {
      handleError(errors, 'PlanDetailsActionsMenu.handleDeleteClick')
    }
  }

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      handleConfirm: onDeletePlan
    })
  }

  const onCloneClick = () => {
    showCloneModal({ plan })
  }

  const onCreateOrDeleteBookmark = async () => {
    const input = {
      planId: plan.id
    }

    await createOrDeleteBookmark(input, plan.isBookmark)

    await planDetailsRefetch()
  }

  const onDataImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    event.preventDefault()

    const file = event.target.files[0]

    if (file) {
      const reader = new FileReader()

      reader.onload = async (e) => {
        try {
          setShowFocuser(true)
          enqueueSnackbar('Extracting data...', {
            key: 'import',
            variant: 'progress',
            autoHideDuration: null
          })

          const xlsx = await import('@e965/xlsx')
          const data = e.target.result
          const workbook = xlsx.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const json = xlsx.utils.sheet_to_json(worksheet)

          await planBulkItemsUpdate({
            variables: { id: plan.id, input: json },
            refetchQueries: [
              {
                query: PLAN_DETAILS_TREE_QUERY,
                variables: {
                  ...getPlanColumnVariables(plan.columnSettings),
                  filter: {
                    planId: plan.id,
                    referenced: router.query.planViewMode === PLAN_VIEW_MODE.PLAN ? false : null
                  }
                }
              }
            ]
          })
        } catch (e) {
          handleError(e, 'PlanDetailsActionsMenu.onDataImport')
        } finally {
          setShowFocuser(false)
          closeSnackbar('import')
        }
      }

      reader.readAsArrayBuffer(file)

      event.target.value = null
    }
  }

  return (
    <>
      <Menu trigger={<DotsIcon htmlColor='white' />} disableAutoFocusItem>
        <span onClick={onCreateOrDeleteBookmark}>
          <BookmarkIcon />
          {plan.isBookmark ? 'Remove from bookmarks' : 'Add to bookmarks'}
        </span>
        {adminLockPlans && (
          <span onClick={() => lockPlan(!plan.locked)}>
            <LockIcon />
            {plan.locked ? 'Unlock' : 'Lock'}
          </span>
        )}
        <span onClick={printPlan}>
          <PrintIcon />
          Print
        </span>
        <span onClick={onCloneClick}>
          <ContentCopyIcon />
          Clone
        </span>
        {planDeletePlan && !plan.locked && (
          <span onClick={onDeleteClick}>
            <DeleteIcon />
            Delete
          </span>
        )}
      </Menu>

      <FileInput
        type='file'
        ref={inputFileRef}
        onChange={onDataImport}
        accept='text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
      />

      {showFocuser && <Focuser />}

      <ConfirmDeleteModal header={`Delete ${plan.name}`}>
        Are you sure want to delete this plan? This will also <b>delete</b> any associated records such as objectives,
        actions and indicators.
      </ConfirmDeleteModal>

      <PlanCloneModal />
    </>
  )
}

export default PlanDetailsActionsMenu
